import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useApp } from "../context/AppContext";
import { PullToRefresh } from "../components/PullToRefresh";
import { useNavigate } from "react-router-dom";
import { financeStats, phaseDistribution, teamDistribution, fmtMoneyShort } from "../lib/appUtils";
import { riskSummary, phaseDurationStats } from "../lib/schedule";
import { Building2, Users, Wallet, Clock, AlertTriangle, ShieldCheck, Sparkles, HardHat, FileDown, FileSpreadsheet, RefreshCw, ArrowUpRight, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, LineChart, Line, ReferenceLine, LabelList } from "recharts";
import { PHASES, TEAMS } from "../data/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "../components/ui/sonner";
import * as XLSX from "xlsx";
import SalesLeaderboard from "../components/SalesLeaderboard";
import BranchPerformance from "../components/BranchPerformance";
// 【2026-10-20 新增】员工使用时长排名摘要卡（仅 admin / CEO 与获授权管理岗可见，无权限整卡不渲染）。
// 数据取 /api/usage/leaderboard，与「系统管理 → 用户管理 → 使用分析」页、导出共用同一后端口径。
import UsageRankCard from "../components/usage/UsageRankCard";
import LowStockCard from "../components/LowStockCard";
import { initTeamFilter, teamFilterKey, isTeamManager } from "../lib/teamScope";

function StatCard({ icon: Icon, label, value, sub, tint, onClick, testid, emphasis = false, delta = null }) {
  const clickable = Boolean(onClick);
  const className = `group relative w-full h-full overflow-hidden rounded-lg border bg-white text-left transition-[transform,box-shadow,border-color] duration-150 ${
    emphasis ? "border-brand-200 shadow-[0_8px_24px_-14px_rgba(79,107,255,0.45)]" : "border-slate-200/90"
  } ${
    clickable
      ? "cursor-pointer hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-[0_12px_26px_-18px_rgba(15,23,42,0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2"
      : ""
  } ${emphasis ? "pl-5 pr-4 py-4 sm:py-5" : "pl-4 pr-3.5 py-3.5"}`;
  const DeltaIcon = !delta ? null : delta.direction === "up" ? TrendingUp : delta.direction === "down" ? TrendingDown : Minus;
  const deltaClass = !delta ? "" : delta.tone === "positive" ? "text-emerald-700 bg-emerald-50" : delta.tone === "negative" ? "text-red-700 bg-red-50" : "text-slate-600 bg-slate-100";
  const content = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className={`${emphasis ? "text-[13px]" : "text-[11px]"} font-semibold text-slate-500`}>{label}</p>
          <p className={`mt-1.5 font-display font-extrabold tabular-nums text-slate-950 ${emphasis ? "text-3xl sm:text-4xl" : "text-2xl"}`}>{value}</p>
        </div>
        <span className={`${emphasis ? "size-10 rounded-lg" : "size-8 rounded-md"} flex shrink-0 items-center justify-center`} style={{ background: tint + "14" }}>
          <Icon className={emphasis ? "size-5" : "size-4"} style={{ color: tint }} aria-hidden="true" />
        </span>
      </div>
      <div className="mt-2 flex min-h-5 flex-wrap items-center gap-x-2 gap-y-1">
        {sub && <p className="text-[11px] leading-4 text-slate-500">{sub}</p>}
        {delta && (
          <span className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${deltaClass}`}>
            <DeltaIcon className="size-3" aria-hidden="true" />
            {delta.label}
          </span>
        )}
        {clickable && <ArrowUpRight className="ml-auto size-3.5 shrink-0 text-slate-300 transition-colors group-hover:text-brand-500" aria-hidden="true" />}
      </div>
    </>
  );
  if (clickable) {
    return (
      <button type="button" data-testid={testid} onClick={onClick} className={className} aria-label={`${label}: ${value}`}>
        {content}
      </button>
    );
  }
  return <div data-testid={testid} className={className}>{content}</div>;
}

function SectionHeading({ title, description, testid }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3" data-testid={testid}>
      <div className="min-w-0">
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        {description && <p className="mt-0.5 text-xs leading-5 text-slate-500">{description}</p>}
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, description, testid }) {
  return (
    <div className="flex min-h-32 flex-col items-center justify-center px-4 py-6 text-center" data-testid={testid}>
      <span className="flex size-9 items-center justify-center rounded-lg bg-slate-100 text-slate-400">
        <Icon className="size-4" aria-hidden="true" />
      </span>
      <p className="mt-2.5 text-sm font-medium text-slate-700">{title}</p>
      {description && <p className="mt-1 max-w-md text-xs leading-5 text-slate-500">{description}</p>}
    </div>
  );
}

const RISK_TINT = { high: "#ef4444", medium: "#d97706", low: "#16a34a" };

export default function Dashboard() {
  const { t, lang, user, allProjects: visibleProjects, api, refresh } = useApp();
  const [trend, setTrend] = useState([]);
  const [trendPhase, setTrendPhase] = useState("all");
  const [phaseFilter, setPhaseFilter] = useState(() => localStorage.getItem("dash_phaseFilter") || "all");
  const [teamFilter, setTeamFilter] = useState(() => initTeamFilter("dash", user));
  useEffect(() => { localStorage.setItem("dash_phaseFilter", phaseFilter); }, [phaseFilter]);
  useEffect(() => { localStorage.setItem(teamFilterKey("dash", user), teamFilter); }, [teamFilter, user]);
  const [escalatedUrges, setEscalatedUrges] = useState([]);
  const [designSum, setDesignSum] = useState(null); // 设计部在途项目（design_projects 口径）
  const [updatedAt, setUpdatedAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();
  const goToModule = (path) => {
    window.dispatchEvent(new CustomEvent("ali:sidebar-focus", { detail: { path } }));
    navigate(path);
  };
  const loadExtras = useCallback(() => Promise.all([
    api.get("/analytics/phase-duration-trend").then(({ data }) => setTrend(data || [])).catch(() => setTrend([])),
    api.get("/analytics/design-summary").then(({ data }) => setDesignSum(data)).catch(() => {}),
    ["admin", "eng_manager", "ceo"].includes(user.role)
      ? api.get("/urges?status=open").then(({ data }) => setEscalatedUrges((data || []).filter((u) => u.escalated))).catch(() => setEscalatedUrges([]))
      : Promise.resolve(),
  ]).then(() => setUpdatedAt(new Date())), [api, user.role]);
  useEffect(() => { loadExtras(); }, [loadExtras]);
  const reload = useCallback(async () => { setRefreshing(true); try { await Promise.all([refresh(), loadExtras()]); } finally { setRefreshing(false); } }, [refresh, loadExtras]);
  // 实时：每 60 秒自动刷新项目 + 统计（页面在后台标签时浏览器会自行降频）
  useEffect(() => { const iv = setInterval(() => { reload(); }, 60000); return () => clearInterval(iv); }, [reload]);

  const trendData = useMemo(() => {
    if (trendPhase === "all") return trend;
    const pi = Number(trendPhase);
    return trend.map((m) => {
      const ph = (m.phases || []).find((p) => p.phaseIndex === pi) || {};
      return { month: m.month, avgStayed: ph.avgStayed || 0, avgStandard: ph.standard || 0,
               avgOverrun: Math.max(0, (ph.avgStayed || 0) - (ph.standard || 0)), overCount: ph.overCount || 0 };
    });
  }, [trend, trendPhase]);

  const [showBaseline, setShowBaseline] = useState(true);
  const trendBaseline = useMemo(() => {
    if (!trendData.length) return { avg: 0, delta: 0 };
    const avg = Math.round(trendData.reduce((s, m) => s + m.avgStayed, 0) / trendData.length);
    const delta = trendData[trendData.length - 1].avgStayed - trendData[0].avgStayed;
    return { avg, delta };
  }, [trendData]);

  const exportTrendExcel = () => {
    const overLabel = trendPhase === "all" ? (lang === "zh" ? "超期阶段数" : "Overdue Phases") : (lang === "zh" ? "超期项目数" : "Overdue Projects");
    const data = trendData.map((m) => ({
      [lang === "zh" ? "月份" : "Month"]: m.month,
      [lang === "zh" ? "平均停留(天)" : "Avg Stayed"]: m.avgStayed,
      [lang === "zh" ? "标准工期(天)" : "Standard"]: m.avgStandard,
      [lang === "zh" ? "平均超期(天)" : "Avg Overrun"]: m.avgOverrun,
      [overLabel]: m.overCount,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, lang === "zh" ? "阶段耗时趋势" : "Phase Trend");
    XLSX.writeFile(wb, `phase_duration_trend_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const exportTrendPdf = async () => {
    try {
      const res = await api.get(`/analytics/phase-duration-trend-pdf?lang=${lang}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "phase_duration_trend.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };
  const canSeeMoney = user.role === "ceo" || user.role === "finance" || user.role === "admin";
  const isDesign = (p) => p.stage === "design" || p.phaseIndex < 0;
  const isCompleted = (p) => p.phaseIndex >= 7;
  const scoped = teamFilter === "all" ? visibleProjects : visibleProjects.filter((p) => p.team === teamFilter); // 统计卡片随团队筛选
  const completedProjects = scoped.filter(isCompleted);
  const engProjectsAll = visibleProjects.filter((p) => !isDesign(p) && !isCompleted(p)); // 团队分布需全量
  const engProjects = scoped.filter((p) => !isDesign(p) && !isCompleted(p)); // in-construction only
  const designActive = designSum ? designSum.active : scoped.filter(isDesign).length; // 设计中 = 设计部在途（全公司），接口未返回前回落旧口径
  const stats = financeStats(scoped);
  const phases = phaseDistribution(engProjects).map((p, i) => ({ ...p, phaseIndex: i }));
  const teams = teamDistribution(engProjectsAll).filter((x) => x.count > 0);
  const collectPct = stats.totalContract ? Math.round((stats.collected / stats.totalContract) * 100) : 0;
  const risk = riskSummary(engProjects);
  const alerts = [...risk.high, ...risk.medium].slice(0, 8);
  const phaseDur = phaseDurationStats(engProjects).map((d) => ({
    ...d,
    name: `${d.phaseIndex + 1}-${lang === "zh" ? PHASES[d.phaseIndex].zh : PHASES[d.phaseIndex].en}`,
    avg: lang === "zh" ? "平均停留" : "Avg Stayed",
  }));
  const onBarPhase = (data, index, e) => {
    e?.stopPropagation?.();
    const pi = data?.phaseIndex ?? data?.payload?.phaseIndex ?? index;
    setPhaseFilter((prev) => (prev === String(pi) ? "all" : String(pi)));
  };
  const teamMax = Math.max(1, ...teams.map((x) => x.count));
  const teamTotal = teams.reduce((s, x) => s + x.count, 0);
  const TEAM_COLORS = ["#4F6BFF", "#7B5CFF", "#06b6d4", "#10b981", "#f59e0b"];
  const fmtTime = (d) => d ? d.toLocaleTimeString(lang === "zh" ? "zh-CN" : "en-US", { hour12: false, timeZone: "America/Los_Angeles" }) : "—";

  return (
    <PullToRefresh onRefresh={reload} zh={lang === "zh"}>
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="sr-only">{t("overview")}</h1>
          <p className="text-[13px] leading-5 text-slate-500">{lang === "zh" ? "公司总体工程与风险实时概览" : "Real-time company-wide project & risk overview"}</p>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-slate-500" data-testid="dash-live-status">
          <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" /></span>
          <span className="hidden sm:inline" data-testid="dash-updated-at">{lang === "zh" ? `每 60 秒刷新 · 更新于 ${fmtTime(updatedAt)}` : `Refreshes every 60s · updated ${fmtTime(updatedAt)}`}</span>
          <button data-testid="dash-refresh-btn" onClick={reload} disabled={refreshing} aria-label={lang === "zh" ? "刷新总览数据" : "Refresh dashboard data"} className="inline-flex min-h-7 items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-slate-600 transition-colors hover:border-brand-300 hover:text-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 disabled:cursor-not-allowed disabled:opacity-50"><RefreshCw className={`size-3.5 ${refreshing ? "animate-spin" : ""}`} /><span className="hidden sm:inline">{lang === "zh" ? "刷新" : "Refresh"}</span></button>
        </div>
      </div>

      {escalatedUrges.length > 0 && (
        <button type="button" data-testid="escalated-alert-banner" onClick={() => navigate("/rectify")} className="flex w-full items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-left transition-colors hover:bg-red-100/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2">
          <span className="relative flex h-3 w-3 flex-shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600" />
          </span>
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-red-700">{lang === "zh" ? `有 ${escalatedUrges.length} 项已升级催办需要你介入督办` : `${escalatedUrges.length} escalated urge(s) need your attention`}</p>
            <p className="text-xs text-red-500 truncate">{escalatedUrges.slice(0, 3).map((u) => u.address).join("、")}{escalatedUrges.length > 3 ? " …" : ""}</p>
          </div>
          <span className="text-sm font-medium text-red-600 whitespace-nowrap">{lang === "zh" ? "前往整改看板 →" : "Go to Rectify →"}</span>
        </button>
      )}

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,2fr)]" aria-labelledby="portfolio-summary-title">
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p id="portfolio-summary-title" className="text-xs font-semibold text-slate-500">{lang === "zh" ? "当前工程组合" : "Active portfolio"}</p>
              <p className="mt-2 font-display text-4xl font-extrabold tabular-nums text-slate-950" data-testid="dash-kpi-total">{designActive + engProjects.length}</p>
              <p className="mt-1 text-xs leading-5 text-slate-500">{lang === "zh" ? "设计部在途 + 在建施工" : "In design plus in construction"}</p>
            </div>
            <span className="flex size-10 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
              <Building2 className="size-5" aria-hidden="true" />
            </span>
          </div>
          <div className="mt-4 grid grid-cols-3 divide-x divide-slate-100 border-t border-slate-100 pt-3">
            <button type="button" data-testid="dash-kpi-design" onClick={() => goToModule("/design/projects")} className="min-w-0 pr-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
              <span className="block text-[11px] text-slate-500">{lang === "zh" ? "设计中" : "In design"}</span>
              <span className="mt-0.5 block text-lg font-bold tabular-nums text-slate-900">{designActive}</span>
            </button>
            <button type="button" data-testid="dash-kpi-construction" onClick={() => goToModule("/board")} className="min-w-0 px-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
              <span className="block text-[11px] text-slate-500">{lang === "zh" ? "在建" : "Building"}</span>
              <span className="mt-0.5 block text-lg font-bold tabular-nums text-slate-900">{engProjects.length}</span>
            </button>
            <button type="button" data-testid="dash-kpi-completed" onClick={() => goToModule("/archive")} className="min-w-0 pl-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
              <span className="block text-[11px] text-slate-500">{lang === "zh" ? "已完成" : "Completed"}</span>
              <span className="mt-0.5 block text-lg font-bold tabular-nums text-slate-900">{completedProjects.length}</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <StatCard icon={AlertTriangle} label={lang === "zh" ? "高风险项目" : "High risk"} value={risk.high.length} sub={engProjects.length ? `${Math.round((risk.high.length / engProjects.length) * 100)}% ${lang === "zh" ? "在建项目" : "of active"}` : (lang === "zh" ? "暂无在建项目" : "No active projects")} tint="#dc2626" onClick={() => goToModule("/board?risk=high")} testid="dash-kpi-high-risk" emphasis={risk.high.length > 0} />
          <StatCard icon={ShieldCheck} label={lang === "zh" ? "进度正常" : "On track"} value={risk.low.length} sub={lang === "zh" ? "按计划推进" : "Following plan"} tint="#15803d" onClick={() => goToModule("/board?risk=low")} testid="dash-kpi-on-track" />
          <StatCard icon={Users} label={t("activeTeams")} value={teams.length} sub={lang === "zh" ? `合计 ${teamTotal} 个在建项目` : `${teamTotal} active projects`} tint="#4F6BFF" onClick={() => goToModule("/board")} testid="dash-kpi-teams" />
          {canSeeMoney && (
            <>
              <StatCard icon={Wallet} label={t("collected")} value={fmtMoneyShort(stats.collected)} sub={lang === "zh" ? "累计已收款" : "Total collected"} tint="#15803d" onClick={() => goToModule("/finance/funds?tab=ar")} testid="dash-kpi-collected" />
              <StatCard icon={Clock} label={t("pendingCollection")} value={fmtMoneyShort(stats.pending)} sub={lang === "zh" ? "待开票 / 待收款" : "Billed and awaiting payment"} tint="#b45309" onClick={() => goToModule("/finance/funds?tab=ar")} testid="dash-kpi-pending" />
              <StatCard icon={TrendingUp} label={lang === "zh" ? "回款率" : "Collection rate"} value={`${collectPct}%`} sub={lang === "zh" ? "已收款 / 合同额" : "Collected / contract"} tint={collectPct >= 40 ? "#15803d" : "#b45309"} onClick={() => goToModule("/finance/funds?tab=ar")} testid="dash-kpi-collection-rate" delta={{ direction: collectPct >= 40 ? "up" : "down", tone: collectPct >= 40 ? "positive" : "negative", label: collectPct >= 40 ? (lang === "zh" ? "健康" : "Healthy") : (lang === "zh" ? "需关注" : "Watch") }} />
            </>
          )}
        </div>
      </section>

      <section className="overflow-hidden rounded-lg border border-slate-200 bg-white" aria-labelledby="risk-title">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-slate-100 px-4 py-3 sm:px-5">
          <Sparkles className="size-4 text-brand-500" aria-hidden="true" />
          <h2 id="risk-title" className="text-sm font-semibold text-slate-900">{lang === "zh" ? "风险与行动" : "Risk & Action"}</h2>
          <span className="text-xs text-slate-500">{lang === "zh" ? "基于施工周期与实际进度分析" : "Schedule versus actual progress"}</span>
          {alerts.length > 0 && <span className="ml-auto rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-red-700">{alerts.length} {lang === "zh" ? "项待处理" : "to review"}</span>}
        </div>
        <div className="p-3 sm:p-4">
          {alerts.length === 0 ? (
            <EmptyState icon={ShieldCheck} title={lang === "zh" ? "当前没有需要处理的风险" : "No risks need attention"} description={lang === "zh" ? "所有在建项目均按计划推进。系统每 60 秒自动更新。" : "All active projects are following the plan. Updates every 60 seconds."} testid="risk-empty-state" />
          ) : (
            <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
              {alerts.map((r) => (
                <button key={r.project.id} type="button" data-testid={`risk-alert-${r.project.id}`} onClick={() => navigate(`/project/${r.project.id}`)} className="flex w-full items-start gap-3 rounded-md border border-slate-200 bg-white p-3 text-left transition-colors hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2">
                  <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-md" style={{ background: RISK_TINT[r.level] + "14" }}>
                    <AlertTriangle className="size-3.5" style={{ color: RISK_TINT[r.level] }} aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-slate-800">{r.project.address}</span>
                    <span className="mt-0.5 block text-xs leading-5 text-slate-600">{lang === "zh" ? r.zh : r.en}</span>
                    <span className="mt-1 block text-[11px] text-slate-400">{r.project.teamName} · {r.project.manager}</span>
                  </span>
                  <ArrowUpRight className="mt-0.5 size-4 shrink-0 text-slate-300" aria-hidden="true" />
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="space-y-3" aria-labelledby="analysis-title">
        <SectionHeading title={lang === "zh" ? "项目分析" : "Project analysis"} description={lang === "zh" ? "按阶段与团队查看在建项目的分布和进度。" : "Review active-project distribution and progress by phase and team."} testid="dashboard-analysis-heading" />

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <div className="rounded-lg border border-slate-200 bg-white p-4 sm:p-5" data-testid="dash-phase-chart">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">{t("projectsByPhase")}</h3>
                <p className="mt-0.5 text-xs leading-5 text-slate-500">{lang === "zh" ? "点击柱状图筛选下方耗时分析" : "Select a bar to filter duration analysis"}</p>
              </div>
              {phaseFilter !== "all" && <button data-testid="dash-phase-clear" onClick={() => setPhaseFilter("all")} className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-brand-600 transition-colors hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">{lang === "zh" ? "清除筛选" : "Clear filter"}</button>}
            </div>
            {phases.every((p) => p.count === 0) ? (
              <EmptyState icon={HardHat} title={lang === "zh" ? "暂无在建项目" : "No active construction"} description={lang === "zh" ? "有项目进入施工阶段后，这里会显示阶段分布。" : "Phase distribution appears when projects enter construction."} testid="dash-phase-empty" />
            ) : (
              <div className="mt-3">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={phases} margin={{ top: 12, right: 4, left: -22, bottom: 32 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                    <XAxis dataKey={lang === "zh" ? "zh" : "en"} tick={{ fontSize: 10, fill: "#64748b" }} angle={-24} textAnchor="end" interval={0} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748b" }} />
                    <Tooltip cursor={{ fill: "#f8fafc" }} />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]} cursor="pointer" onClick={onBarPhase}>
                      {phases.map((p, i) => <Cell key={i} fill={p.color} fillOpacity={phaseFilter === "all" || phaseFilter === String(p.phaseIndex) ? 1 : 0.25} />)}
                      <LabelList dataKey="count" position="top" style={{ fontSize: 11, fontWeight: 700, fill: "#334155" }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-4 sm:p-5" data-testid="dash-team-chart">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">{t("projectsByTeam")}</h3>
                <p className="mt-0.5 text-xs leading-5 text-slate-500">{lang === "zh" ? "在建项目按工程项目组分布" : "Active projects by engineering team"}</p>
              </div>
              <div className="flex items-center gap-2">
                <Select value={teamFilter} onValueChange={setTeamFilter}>
                  <SelectTrigger className="h-8 w-32 text-xs" data-testid="dash-team-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{lang === "zh" ? "全部团队" : "All Teams"}</SelectItem>
                    {TEAMS.map((tm) => <SelectItem key={tm.id} value={tm.id} data-testid={`dash-team-filter-opt-${tm.id}`}>{lang === "zh" ? tm.zh : tm.name}{!isTeamManager(user) && tm.id === user.team ? (lang === "zh" ? "（本团队）" : " (mine)") : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                {teamFilter !== "all" && <button data-testid="dash-team-clear" onClick={() => setTeamFilter("all")} className="rounded-md px-2 py-1 text-xs font-medium text-brand-600 transition-colors hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">{lang === "zh" ? "清除" : "Clear"}</button>}
              </div>
            </div>
            {teams.length === 0 ? (
              <EmptyState icon={Users} title={lang === "zh" ? "暂无团队项目" : "No team projects"} description={lang === "zh" ? "当前筛选条件下没有在建施工项目。" : "No active construction projects match the current filter."} testid="dash-team-bars-empty" />
            ) : (
              <div className="mt-4 space-y-2.5" data-testid="dash-team-bars">
                {teams.map((tm) => {
                  const color = TEAM_COLORS[TEAMS.findIndex((x) => x.id === tm.id) % TEAM_COLORS.length];
                  const dim = teamFilter !== "all" && teamFilter !== tm.id;
                  const pct = teamTotal ? Math.round((tm.count / teamTotal) * 100) : 0;
                  return (
                    <button key={tm.id} type="button" data-testid={`dash-team-bar-${tm.id}`} data-count={tm.count} data-active={teamFilter === tm.id ? "1" : "0"}
                      onClick={() => setTeamFilter((prev) => (prev === tm.id ? "all" : tm.id))}
                      className={`w-full rounded-md border px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${teamFilter === tm.id ? "border-brand-200 bg-brand-50" : "border-transparent hover:border-slate-200 hover:bg-slate-50"} ${dim ? "opacity-45" : ""}`}>
                      <div className="flex items-center justify-between gap-3 text-sm">
                        <span className="min-w-0 truncate font-medium text-slate-800">{lang === "zh" ? tm.zh : tm.name}</span>
                        <span className="shrink-0 text-xs tabular-nums text-slate-500"><b className="text-sm text-slate-900">{tm.count}</b> {lang === "zh" ? `个 · ${pct}%` : `· ${pct}%`}</span>
                      </div>
                      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full transition-[width] duration-200" style={{ width: `${Math.round((tm.count / teamMax) * 100)}%`, background: color }} /></div>
                    </button>
                  );
                })}
                <p className="pt-0.5 text-right text-[11px] text-slate-400" data-testid="dash-team-bars-total">{lang === "zh" ? `合计 ${teamTotal} 个在建项目` : `${teamTotal} active projects in total`}</p>
              </div>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-4 sm:p-5 xl:col-span-2" data-testid="dash-team-comparison">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">{t("teamComparison")}</h3>
                <p className="mt-0.5 text-xs leading-5 text-slate-500">{lang === "zh" ? "各工程项目组的平均施工进度" : "Average construction progress by engineering team"}</p>
              </div>
              {teamFilter !== "all" && <button data-testid="dash-team-comparison-clear" onClick={() => setTeamFilter("all")} className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-brand-600 transition-colors hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">{lang === "zh" ? "清除筛选" : "Clear filter"}</button>}
            </div>
            {teams.length === 0 ? (
              <EmptyState icon={TrendingUp} title={lang === "zh" ? "暂无团队进度数据" : "No team progress data"} description={lang === "zh" ? "有在建施工项目后，这里会显示各工程项目组的平均进度。" : "Average progress by team appears once projects are in construction."} testid="dash-team-comparison-empty" />
            ) : (
              <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2" data-testid="dash-team-comparison-list">
                {teams.map((tm) => {
                  const color = TEAM_COLORS[TEAMS.findIndex((x) => x.id === tm.id) % TEAM_COLORS.length];
                  const dim = teamFilter !== "all" && teamFilter !== tm.id;
                  return (
                    <button key={tm.id} type="button" data-testid={`dash-team-compare-${tm.id}`} data-progress={tm.avgProgress}
                      onClick={() => setTeamFilter((prev) => (prev === tm.id ? "all" : tm.id))}
                      className={`rounded-md border px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${teamFilter === tm.id ? "border-brand-200 bg-brand-50" : "border-transparent hover:border-slate-200 hover:bg-slate-50"} ${dim ? "opacity-45" : ""}`}>
                      <div className="flex items-center justify-between gap-3 text-sm">
                        <span className="min-w-0 truncate font-medium text-slate-800">{lang === "zh" ? tm.zh : tm.name}</span>
                        <span className="shrink-0 text-xs tabular-nums text-slate-500"><b className="text-sm text-slate-900">{tm.avgProgress}%</b></span>
                      </div>
                      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full transition-[width] duration-200" style={{ width: `${tm.avgProgress}%`, background: color }} /></div>
                      <p className="mt-1 text-[11px] text-slate-400">{tm.count} {lang === "zh" ? "个在建项目" : "active projects"}</p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4 sm:p-5" aria-labelledby="phase-duration-title">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 id="phase-duration-title" className="text-sm font-semibold text-slate-900">{lang === "zh" ? "各施工阶段耗时分析" : "Phase duration analysis"}</h2>
            <p className="mt-0.5 text-xs leading-5 text-slate-500">{lang === "zh" ? "平均停留天数与标准工期对比，红色表示超出标准" : "Average days compared with the standard; red exceeds the standard"}</p>
          </div>
          {phaseFilter !== "all" && <button data-testid="phase-dur-clear" onClick={() => setPhaseFilter("all")} className="rounded-md px-2 py-1 text-xs font-medium text-brand-600 transition-colors hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">{lang === "zh" ? "清除阶段筛选" : "Clear phase filter"}</button>}
        </div>
        {phaseDur.every((d) => d.count === 0) ? (
          <EmptyState icon={Clock} title={lang === "zh" ? "暂无可分析的阶段耗时" : "No phase duration data"} description={lang === "zh" ? "项目进入施工并产生阶段耗时后，这里会显示对比分析。" : "Duration comparison appears once active projects accumulate phase time."} testid="phase-duration-empty" />
        ) : (
          <div className="mt-3">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={phaseDur} margin={{ top: 12, right: 8, left: -12, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} angle={-20} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 10, fill: "#64748b" }} />
                <Tooltip formatter={(v, n) => [`${v} ${lang === "zh" ? "天" : "d"}`, n]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="standard" name={lang === "zh" ? "标准工期" : "Standard"} fill="#cbd5e1" radius={[3, 3, 0, 0]}>
                  {phaseDur.map((d, i) => <Cell key={i} fill="#cbd5e1" fillOpacity={phaseFilter === "all" || phaseFilter === String(d.phaseIndex) ? 1 : 0.28} />)}
                </Bar>
                <Bar dataKey="avgStayed" name={lang === "zh" ? "平均停留" : "Avg Stayed"} radius={[3, 3, 0, 0]}>
                  {phaseDur.map((d, i) => <Cell key={i} fill={d.avgStayed > d.standard ? "#dc2626" : "#15803d"} fillOpacity={phaseFilter === "all" || phaseFilter === String(d.phaseIndex) ? 1 : 0.28} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      {trend.length > 0 && (
        <section className="rounded-lg border border-slate-200 bg-white p-4 sm:p-5" data-testid="phase-duration-trend-chart" aria-labelledby="phase-trend-title">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 id="phase-trend-title" className="text-sm font-semibold text-slate-900">{lang === "zh" ? "阶段耗时趋势" : "Phase duration trend"}</h2>
              <p className="mt-0.5 text-xs leading-5 text-slate-500">{trendPhase === "all" ? (lang === "zh" ? "各月平均停留天数与超期趋势，数值下降代表效率提升" : "Monthly average days and overrun; lower is better") : (lang === "zh" ? `仅看「${PHASES[Number(trendPhase)].zh}」阶段` : `Single phase: ${PHASES[Number(trendPhase)].en}`)}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-semibold tabular-nums ${trendBaseline.delta <= 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`} data-testid="trend-delta-badge">
                  {trendBaseline.delta <= 0 ? <TrendingDown className="size-3" aria-hidden="true" /> : <TrendingUp className="size-3" aria-hidden="true" />} {lang === "zh" ? "较首月" : "vs first"} {Math.abs(trendBaseline.delta)}{lang === "zh" ? "天" : "d"} {trendBaseline.delta <= 0 ? (lang === "zh" ? "改善" : "better") : (lang === "zh" ? "恶化" : "worse")}
                </span>
                <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-slate-500" data-testid="trend-baseline-toggle">
                  <input type="checkbox" checked={showBaseline} onChange={(e) => setShowBaseline(e.target.checked)} className="accent-brand-500" />
                  {lang === "zh" ? `期间均值基线 (${trendBaseline.avg}天)` : `Period baseline (${trendBaseline.avg}d)`}
                </label>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={trendPhase} onValueChange={setTrendPhase}>
                <SelectTrigger className="h-8 w-36 text-xs" data-testid="trend-phase-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{lang === "zh" ? "全部（平均）" : "All (average)"}</SelectItem>
                  {PHASES.map((ph, i) => <SelectItem key={ph.key} value={String(i)}>{i + 1}-{lang === "zh" ? ph.zh : ph.en}</SelectItem>)}
                </SelectContent>
              </Select>
              <button data-testid="trend-export-excel-btn" onClick={exportTrendExcel} className="inline-flex min-h-8 items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-brand-300 hover:text-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
                <FileSpreadsheet className="size-3.5" aria-hidden="true" /> Excel
              </button>
              <button data-testid="trend-export-pdf-btn" onClick={exportTrendPdf} className="inline-flex min-h-8 items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-brand-300 hover:text-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
                <FileDown className="size-3.5" aria-hidden="true" /> PDF
              </button>
            </div>
          </div>
          <div className="mt-3">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={trendData} margin={{ top: 12, right: 10, left: -12, bottom: 6 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 10, fill: "#64748b" }} />
                <Tooltip formatter={(v, n) => [`${v}${lang === "zh" ? " 天" : "d"}`, n]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {showBaseline && <ReferenceLine y={trendBaseline.avg} stroke="#7B5CFF" strokeDasharray="4 4" label={{ value: lang === "zh" ? `均值 ${trendBaseline.avg}` : `avg ${trendBaseline.avg}`, position: "insideTopRight", fontSize: 10, fill: "#7B5CFF" }} />}
                <Line type="monotone" dataKey="avgStayed" name={lang === "zh" ? "平均停留" : "Avg Stayed"} stroke="#4F6BFF" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="avgStandard" name={lang === "zh" ? "标准工期" : "Standard"} stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 4" dot={false} />
                <Line type="monotone" dataKey="avgOverrun" name={lang === "zh" ? "平均超期" : "Avg Overrun"} stroke="#d97706" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {["admin", "ceo"].includes(user.role) && (
        <section className="space-y-3" aria-labelledby="business-detail-title">
          <SectionHeading title={lang === "zh" ? "业务明细" : "Business detail"} description={lang === "zh" ? "分公司、销售和仓储数据。" : "Branch, sales, and warehouse detail."} />
          <div className="space-y-4">
            <BranchPerformance />
            <SalesLeaderboard />
            {/* 员工使用排名：需求要求「总览只展示摘要、不放大图表」，
                所以这里只有前 10 名的小表格 + 时间范围切换，完整图表留在使用分析页。
                无权限时该组件自身返回 null（不渲染空壳）。 */}
            <UsageRankCard />
            <LowStockCard />
          </div>
        </section>
      )}
    </div>
    </PullToRefresh>
  );
}
